(() => {
  'use strict';
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const config = (() => { try { return JSON.parse(document.getElementById('DropkyConfig')?.textContent || '{}'); } catch { return {}; } })();
  function checkoutUrl(value) {
    try {
      if (!config.checkoutEnabled || typeof value !== 'string') return '';
      value = value.trim();
      if (!value || value.includes('\\') || !(value.startsWith('https://') || (value.startsWith('/') && !value.startsWith('//')))) return '';
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || url.username || url.password) return '';
      if (url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkout|checkouts|pages\/(?:pay|checkout))(?:\/|$)/i.test(decodeURIComponent(url.pathname))) return '';
      if (url.hostname === 'dropky-checkout.shopitropa.workers.dev') return '';
      return value;
    } catch { return ''; }
  }
  function analyticsAllowed() {
    return config.analytics && !config.designMode && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  }
  function track(event, data) {
    if (!analyticsAllowed()) return false;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...data });
      window.dispatchEvent(new CustomEvent('dropky:analytics', { detail: { event, ...data } }));
      return true;
    } catch { return false; }
  }
  if (config.analytics && !window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => document.dispatchEvent(new Event('dropky:privacy-ready')));
  }
  const seenProducts = new Set();

  class DropkyHeader extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.menu = this.querySelector('[data-menu-panel]');
      this.search = this.querySelector('[data-search-panel]');
      this.menuButton = this.querySelector('[data-open-menu]');
      this.searchButton = this.querySelector('[data-open-search]');
      this.closest('.shopify-section')?.classList.toggle('dk-header-wrap--sticky', this.classList.contains('dk-header--sticky'));
      this.menuButton?.addEventListener('click', () => this.toggle('menu'), opts);
      this.searchButton?.addEventListener('click', () => this.toggle('search'), opts);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const opener = !this.search?.hidden ? this.searchButton : this.menuButton;
          this.close(); opener?.focus();
        }
      }, opts);
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); }, opts);
      this.menu?.addEventListener('click', (event) => { if (event.target.closest('a')) this.close(); }, opts);
    }
    toggle(which) {
      const panel = this[which];
      if (!panel) return;
      const open = panel.hidden;
      this.close();
      panel.hidden = !open;
      this[`${which}Button`]?.setAttribute('aria-expanded', String(open));
      if (open && which === 'search') panel.querySelector('input')?.focus();
    }
    close() {
      for (const key of ['menu', 'search']) {
        if (this[key]) this[key].hidden = true;
        this[`${key}Button`]?.setAttribute('aria-expanded', 'false');
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class DropkySearch extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[role="listbox"]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;
      this.active = -1;
      this.cache = new Map();
      const opts = { signal: this.abort.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.request?.abort();
        this.close();
        const query = this.input.value.trim();
        if (query.length < 2) return;
        this.timer = setTimeout(() => this.load(query), 180);
      }, opts);
      this.input.addEventListener('keydown', (event) => this.keydown(event), opts);
      this.input.addEventListener('focus', () => { if (this.results.children.length && this.input.value.trim().length >= 2) this.open(); }, opts);
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); }, opts);
      this.results.addEventListener('pointermove', (event) => {
        const hit = event.target.closest('[role="option"]');
        if (hit) this.activate(this.options().indexOf(hit));
      }, opts);
      this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) this.close(); }, opts);
    }
    async load(query) {
      if (this.cache.has(query)) { this.render(this.cache.get(query)); return; }
      this.request = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Căutăm jocul…';
      try {
        const endpoint = new URL(`${this.dataset.endpoint || '/search/suggest'}.json`, location.origin);
        endpoint.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title' }).toString();
        const response = await fetch(endpoint, { signal: this.request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search');
        const json = await response.json();
        const products = json.resources?.results?.products || [];
        if (query !== this.input.value.trim()) return;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, products);
        this.render(products);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.close();
          this.status.textContent = 'Apasă Enter pentru a vedea rezultatele căutării.';
        }
      } finally { this.results.removeAttribute('aria-busy'); }
    }
    render(products) {
      this.results.replaceChildren();
      for (const [index, product] of products.entries()) {
        let url;
        try { url = new URL(product.url, location.origin); } catch { continue; }
        if (url.origin !== location.origin || !/(?:^|\/)products\//.test(url.pathname)) continue;
        const anchor = document.createElement('a');
        anchor.href = url.href; anchor.className = 'dk-search-result';
        anchor.id = `${this.results.id}-${index}`;
        anchor.setAttribute('role', 'option'); anchor.setAttribute('aria-selected', 'false'); anchor.tabIndex = -1;
        const imageUrl = product.featured_image?.url || product.image;
        if (imageUrl) {
          let parsed;
          try { parsed = new URL(imageUrl, location.origin); } catch { parsed = null; }
          if (parsed && ['https:', 'http:'].includes(parsed.protocol)) {
            const image = document.createElement('img'); image.src = parsed.href; image.width = 40; image.height = 52; image.alt = ''; image.loading = 'lazy';
            anchor.append(image);
          }
        }
        const title = document.createElement('span'); title.textContent = product.title; anchor.append(title);
        this.results.append(anchor);
      }
      const count = this.options().length;
      if (!count) { const empty = document.createElement('p'); empty.className = 'dk-search-message'; empty.textContent = 'Nu am găsit jocul. Încearcă un alt nume.'; this.results.append(empty); }
      this.status.textContent = `${count} jocuri găsite.`;
      this.active = -1; this.input.removeAttribute('aria-activedescendant'); this.open();
    }
    options() { return [...this.results.querySelectorAll('[role="option"]')]; }
    open() { this.results.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    close() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); this.active = -1; }
    activate(index) {
      const options = this.options(); if (!options.length) return;
      this.active = (index + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.active)));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
      options[this.active].scrollIntoView({ block: 'nearest' });
    }
    keydown(event) {
      if (event.key === 'Escape') { this.close(); return; }
      if (this.results.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); this.activate(this.active + (event.key === 'ArrowDown' ? 1 : -1)); }
      if (event.key === 'Enter' && this.active >= 0) { event.preventDefault(); this.options()[this.active]?.click(); }
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); clearTimeout(this.timer); }
  }

  class DropkyGallery extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.track = this.querySelector('[data-gallery-track]');
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.image = this.querySelector('[data-zoom-image]');
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.move(-1), opts);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.move(1), opts);
      this.track?.addEventListener('pointerdown', (event) => { this.pointerStart = event.clientX; }, opts);
      this.track?.addEventListener('click', (event) => {
        if (Math.abs(event.clientX - (this.pointerStart ?? event.clientX)) > 12) return;
        const slide = event.target.closest('[data-zoom-src]');
        if (!slide || !this.dialog?.showModal || !this.image) return;
        this.openImage(slide.dataset.zoomSrc, slide.querySelector('img')?.alt || '');
      }, opts);
      this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog?.close(), opts);
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, opts);
      this.track?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
      }, opts);
    }
    openImage(src, alt) {
      if (!src || !this.dialog?.showModal || !this.image) return;
      this.image.src = src; this.image.alt = alt;
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.querySelector('button')?.focus();
    }
    move(direction) {
      if (!this.track) return;
      const last = this.track.children.length - 1;
      const current = Math.round(this.track.scrollLeft / this.track.clientWidth);
      const index = Math.max(0, Math.min(last, current + direction));
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    showMedia(id) {
      if (!id || !this.track) return;
      const slides = [...this.track.children]; const index = slides.findIndex(slide => String(slide.dataset.mediaId) === String(id));
      if (index >= 0) this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
    disconnectedCallback() { this.abort?.abort(); if (this.dialog?.open) this.dialog.close(); }
  }

  class DropkyProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      try { this.data = JSON.parse(this.querySelector('[data-product-data]').textContent); } catch { return; }
      this.data.variants.forEach(v => { v.checkoutUrl = checkoutUrl(v.checkoutUrl); });
      this.variant = this.data.variants.find(v => String(v.id) === String(this.data.initialVariant)) || null;
      this.selectors = [...this.querySelectorAll('[data-option-index]')];
      this.addEventListener('change', (event) => { if (event.target.matches('.dk-option input[type="radio"]')) this.selectVariant(); }, opts);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cover-zoom]')) {
          const cover = this.querySelector('[data-product-cover]');
          this.querySelector('dk-gallery')?.openImage(this.variant?.coverZoom || cover?.src, cover?.alt || this.data.title);
        } else this.buy(event);
      }, opts);
      window.addEventListener('pageshow', () => this.refresh(), opts);
      this.viewProduct();
      document.addEventListener('visitorConsentCollected', () => this.viewProduct(), opts);
      document.addEventListener('dropky:privacy-ready', () => this.viewProduct(), opts);
      this.refresh();
      this.querySelector('dk-gallery')?.showMedia(this.variant?.mediaId || this.data.defaultMediaId);
      this.initSticky();
    }
    eventData() {
      return { product_id: this.data.id, product_name: this.data.title, variant_id: this.variant?.id || null, variant_name: this.variant?.title || null, value: this.variant ? this.variant.price / 100 : null, currency: this.data.currency, quantity: 1 };
    }
    viewProduct() {
      if (!seenProducts.has(this.data.id) && track('dropky_product_view', this.eventData())) seenProducts.add(this.data.id);
    }
    selectVariant() {
      const values = this.selectors.map(field => field.querySelector('input:checked')?.value || '');
      this.variant = this.data.variants.find(v => v.options.every((value, index) => value === values[index])) || null;
      this.refresh();
      this.querySelector('dk-gallery')?.showMedia(this.variant?.mediaId || this.data.defaultMediaId);
      const url = new URL(location.href);
      if (this.variant) url.searchParams.set('variant', this.variant.id); else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
      track('dropky_select_variant', this.eventData());
      this.dispatchEvent(new CustomEvent('dropky:variant-change', { bubbles: true, detail: { productId: this.data.id, variantId: this.variant?.id || null } }));
    }
    refresh() {
      const v = this.variant; const canBuy = !!(v?.available && v.checkoutUrl);
      const hasDiscount = v && v.compare > v.price;
      this.querySelectorAll('[data-price]').forEach(slot => { slot.textContent = v?.priceText || '—'; });
      this.querySelectorAll('[data-euro]').forEach(slot => {
        slot.hidden = !v?.euroText;
        const label = slot.querySelector('span')?.textContent || '· plată în EUR';
        if (v?.euroText) slot.replaceChildren(document.createTextNode(v.euroText + ' '), Object.assign(document.createElement('span'), { textContent: label }));
      });
      this.querySelectorAll('[data-summary-index]').forEach(slot => { slot.textContent = v?.options[Number(slot.dataset.summaryIndex)] || '—'; });
      const cover = this.querySelector('[data-product-cover]');
      if (cover && v) cover.alt = this.data.defaultVariant ? this.data.title : `${this.data.title} · ${v.title}`;
      if (cover && v?.coverUrl && cover.getAttribute('src') !== v.coverUrl) {
        cover.removeAttribute('srcset'); cover.src = v.coverUrl;
      }
      this.querySelectorAll('[data-compare]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = v?.compareText || ''; });
      this.querySelectorAll('[data-discount]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = hasDiscount ? `−${Math.round((v.compare - v.price) / v.compare * 100)}%` : ''; });
      this.querySelectorAll('[data-savings]').forEach(slot => { slot.hidden = !hasDiscount; slot.textContent = hasDiscount ? v.savingsText : ''; });
      this.querySelectorAll('[data-checkout-cta]').forEach(cta => {
        cta.removeAttribute('aria-busy');
        if (canBuy) { cta.href = v.checkoutUrl; cta.removeAttribute('aria-disabled'); cta.removeAttribute('tabindex'); }
        else { cta.removeAttribute('href'); cta.setAttribute('aria-disabled', 'true'); cta.tabIndex = -1; }
        const label = cta.querySelector('[data-buy-label]'); if (label) label.textContent = canBuy ? this.data.buyText : this.data.unavailableText;
      });
      const available = this.querySelector('[data-availability]');
      if (available) available.textContent = !v ? this.data.invalidText : !v.available ? this.data.soldOutText : !config.checkoutEnabled ? config.pendingText : !v.checkoutUrl ? this.data.missingText : this.data.availableText;
      const values = this.selectors.map(field => field.querySelector('input:checked')?.value || '');
      for (const [index, field] of this.selectors.entries()) {
        const currentLabel = field.querySelector('[data-option-label]'); if (currentLabel) currentLabel.textContent = values[index];
        for (const input of field.querySelectorAll('input')) {
          const candidate = this.data.variants.find(candidate => candidate.options.every((value, i) => value === (i === index ? input.value : values[i])));
          const possible = !!candidate?.available;
          const price = input.closest('label').querySelector('[data-option-price]');
          if (price) price.textContent = candidate?.priceText || '—';
          input.closest('label').classList.toggle('is-unavailable', !possible);
        }
      }
      this.querySelectorAll('[data-selection]').forEach(slot => { slot.textContent = this.data.defaultVariant ? this.data.title : values.join(' · '); });
      const status = this.querySelector('[data-variant-status]');
      if (status) status.textContent = v ? `${v.title === 'Default Title' ? this.data.title : v.title}. ${v.priceText}. ${canBuy ? 'Disponibil.' : this.data.unavailableText}` : 'Combinație indisponibilă.';
    }
    buy(event) {
      const cta = event.target.closest('[data-checkout-cta]'); if (!cta) return;
      const v = this.variant;
      if (!v?.available || !checkoutUrl(v.checkoutUrl) || cta.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      cta.href = v.checkoutUrl;
      const data = { ...this.eventData(), checkout_type: 'manual_payment_link' };
      track('dropky_buy_now_click', data);
      track('dropky_checkout_outbound', data);
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        cta.setAttribute('aria-busy', 'true'); cta.querySelector('[data-buy-label]').textContent = config.loadingText || 'Deschidem checkout-ul…';
      }
    }
    initSticky() {
      this.sticky = this.querySelector('[data-sticky-buy]');
      const main = this.querySelector('[data-purchase] [data-checkout-cta]');
      if (!this.sticky || !main || !window.IntersectionObserver) return;
      this.mainVisible = true; this.pageVisible = true;
      const update = () => {
        const show = !this.mainVisible && this.pageVisible && matchMedia('(max-width:760px)').matches;
        this.sticky.hidden = !show;
        document.body.classList.toggle('dk-has-sticky', show);
        if (show) document.body.style.setProperty('--dk-sticky-space', `${Math.ceil(this.sticky.getBoundingClientRect().height)}px`);
      };
      this.ctaObserver = new IntersectionObserver(entries => { this.mainVisible = entries[0].isIntersecting; update(); }, { threshold: 0 });
      this.pageObserver = new IntersectionObserver(entries => { this.pageVisible = entries[0].isIntersecting; update(); }, { threshold: 0 });
      this.ctaObserver.observe(main); this.pageObserver.observe(this);
      if (window.ResizeObserver) {
        this.stickyResize = new ResizeObserver(() => { if (!this.sticky.hidden) update(); });
        this.stickyResize.observe(this.sticky, { box: 'border-box' });
      }
      window.addEventListener('resize', update, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.ctaObserver?.disconnect(); this.pageObserver?.disconnect(); this.stickyResize?.disconnect(); document.body.classList.remove('dk-has-sticky'); document.body.style.removeProperty('--dk-sticky-space'); }
  }
  for (const [name, element] of [['dk-header',DropkyHeader],['dk-search',DropkySearch],['dk-gallery',DropkyGallery],['dk-product',DropkyProduct]]) {
    if (!customElements.get(name)) customElements.define(name, element);
  }
  document.addEventListener('change', event => { if (event.target.matches('[data-auto-submit]')) event.target.form?.requestSubmit(); });
  document.addEventListener('click', event => {
    const anchor = event.target.closest('a[data-jump]'); if (!anchor) return;
    const target = document.querySelector(anchor.dataset.jump === 'faq' ? '[data-faq-anchor]' : '[data-selection-anchor]');
    if (target) { event.preventDefault(); target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' }); }
  });
  if (location.hash === '#faq' || location.hash === '#selectie') {
    requestAnimationFrame(() => document.querySelector(location.hash === '#faq' ? '[data-faq-anchor]' : '[data-selection-anchor]')?.scrollIntoView({ behavior: 'instant' }));
  }
})();
